<div #root class="people-picker-field" [class.disabled]="disabled" [class.error]="showError">

    <!-- Label -->
    <label class="people-picker-label" *ngIf="label"
           [class.error]="showError">
        {{ label }}<span *ngIf="required" aria-hidden="true"> *</span>
    </label>

    <div class="combo">
        <!-- Input Wrap (chips + text input) -->
        <div #overlayOrigin
             cdkOverlayOrigin
             #ddTrigger="cdkOverlayOrigin"
             class="people-picker-input-wrap combo-box"
             [class.focused]="isFocused"
             [class.disabled]="disabled"
             [class.error]="showError"
             (click)="focusInput()">

            <!-- Chips -->
            <div class="people-picker-chip"
                 *ngFor="let sel of selections; let i = index; trackBy: trackBySelection"
                 [class.chip-email]="sel.type === 'email'"
                 [class.invalid]="sel.type === 'email' && !sel.valid"
                 [class.disabled]="disabled"
                 [class.highlighted]="highlightedChipIndex === i">

                <span class="chip-avatar"
                      *ngIf="sel.type === 'person'"
                      [style.background-color]="sel.person.initialsColor">
                    {{ sel.person.initials }}
                </span>

                <i class="chip-icon ph-bold ph-envelope-simple icon-16" *ngIf="sel.type === 'email'"
                   aria-hidden="true"></i>

                <span class="chip-label">
                    {{ sel.type === 'person'
                        ? (chipDisplay === 'email' && sel.person.email ? sel.person.email : sel.person.name)
                        : sel.email }}
                </span>

                <button class="chip-remove" type="button"
                        *ngIf="!disabled"
                        [attr.aria-label]="'Remove ' + (sel.type === 'person' ? sel.person.name : sel.email)"
                        (click)="removeSelection(i); $event.stopPropagation()">
                    <i class="ph-bold ph-x" aria-hidden="true"></i>
                </button>
            </div>

            <!-- Text Input -->
            <input #searchInput
                   class="people-picker-text-input combo-input"
                   type="text"
                   [placeholder]="currentPlaceholder"
                   [formControl]="searchControl"
                   (focus)="onFocus()"
                   (blur)="onBlur()"
                   (keydown)="onKeydown($event)"
                   [disabled]="disabled || atMax">

            <span class="people-picker-max-badge" *ngIf="maxSelections > 0 && !singleSelect">
                {{ selections.length }}/{{ maxSelections }}
            </span>
        </div>

        <!-- Shared dropdown body — rendered either through a CDK overlay (default) or inline
             in the host flow ([renderInline]) so a dialog picker's list scrolls with the modal
             instead of floating over its title (SD-8041). -->
        <ng-template #ddPanelBody>

            <!-- Tag filter (optional) -->
            <div class="dd-filter"
                 *ngIf="enableTagFilter && availableTags.length">
                <div class="dd-filter-head">
                    <span class="dd-filter-title" [class.on]="tagFilterCount > 0">
                        {{ tagFilterLabel }}
                        <span class="fcount" *ngIf="tagFilterCount > 0">{{ tagFilterCount }}</span>
                    </span>
                    <button type="button"
                            class="dd-filter-clear"
                            *ngIf="tagFilterCount > 0"
                            (click)="clearTagFilters()">
                        Clear filters
                    </button>
                </div>
                <div class="dd-filter-scroll">
                    <button type="button"
                            class="tpill"
                            *ngFor="let tag of orderedAvailableTags; trackBy: trackByTagName"
                            [attr.data-state]="getTagState(tag.tag)"
                            (click)="cycleTag(tag.tag)">
                        <span class="tp-ind"></span>
                        <span class="tp-mode" *ngIf="getTagState(tag.tag) === 'not'">not</span>
                        <span>{{ tag.tag }}</span>
                    </button>
                </div>
            </div>

            <!-- Hint text (only when tag filter is enabled) -->
            <div class="dd-hint" *ngIf="enableTagFilter && availableTags.length">
                <i class="hint-icon ph-bold ph-info icon-muted icon-12" aria-hidden="true"></i>
                <span>Tag: click to <b>require</b><span class="sep">·</span><b class="exclude">exclude</b><span class="sep">·</span>clear. Then click a person to add them.</span>
            </div>

            <!-- Loading state -->
            <div class="dd-loading" *ngIf="isLoading">
                <mat-spinner diameter="20"></mat-spinner>
                <span>Loading people…</span>
            </div>

            <!-- Results header -->
            <div class="dd-results-head" *ngIf="!isLoading">
                <span class="count">
                    <b>{{ filteredPeople.length }}</b>
                    {{ filteredPeople.length === 1 ? 'person' : 'people' }}
                </span>
                <span class="filtered-note" *ngIf="filteredNoteText">{{ filteredNoteText }}</span>
            </div>

            <!-- List -->
            <div class="dd-list" *ngIf="!isLoading">
                <div class="dd-item"
                     *ngFor="let person of filteredPeople; trackBy: trackByPerson"
                     [class.selected]="isPersonSelected(person.id)"
                     [class.disabled]="person.disabled"
                     [matTooltip]="person.disabledReason"
                     [matTooltipDisabled]="!person.disabled"
                     (click)="togglePerson(person)">

                    <div class="dd-avatar">
                        {{ person.initials }}
                    </div>

                    <div class="dd-main">
                        <div class="dd-top">
                            <span class="dd-name">{{ person.name }}</span>
                        </div>
                        <div class="dd-sub" *ngIf="person.email || person.meta">
                            {{ person.email || person.meta }}
                        </div>
                    </div>

                    <div class="dd-tags" *ngIf="enableTagFilter && person.tags?.length">
                        <span class="dd-tag"
                              *ngFor="let t of getVisibleTags(person); trackBy: trackByTagName"
                              [class.match]="isTagRequired(t)">
                            {{ t }}
                        </span>
                        <span class="dd-tag more" *ngIf="getExtraTagCount(person) > 0">
                            +{{ getExtraTagCount(person) }}
                        </span>
                    </div>

                    <mat-radio-button class="dd-ck"
                                      *ngIf="singleSelect"
                                      [disableRipple]="true"
                                      [checked]="isPersonSelected(person.id)"
                                      aria-hidden="true"
                                      tabindex="-1">
                    </mat-radio-button>
                    <mat-checkbox class="dd-ck"
                                  *ngIf="!singleSelect"
                                  [disableRipple]="true"
                                  [checked]="isPersonSelected(person.id)"
                                  aria-hidden="true"
                                  tabindex="-1">
                    </mat-checkbox>
                </div>

                <!-- Empty state -->
                <div class="dd-empty" *ngIf="!filteredPeople.length && !hasEmailAction">
                    <div class="et">No people match</div>
                    <div>Adjust your tag filters or search</div>
                </div>

                <!-- Freeform email action -->
                <div class="dd-item dd-email-action"
                     *ngIf="hasEmailAction"
                     (click)="commitFreeformEmail(currentEmailInput)">
                    <div class="dd-avatar email-avatar">
                        <i class="ph-bold ph-envelope-simple icon-16" aria-hidden="true"></i>
                    </div>
                    <div class="dd-main">
                        <div class="dd-top">
                            <span class="dd-name">Add "{{ currentEmailInput }}"</span>
                        </div>
                        <div class="dd-sub">
                            {{ isValidEmail(currentEmailInput) ? 'Send to this email address' : 'Finish typing to add as email' }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Bulk actions (multi-select only) -->
            <div class="dd-bulk" *ngIf="!singleSelect && !isLoading && (filteredPeople.length || selections.length)">
                <span class="dd-bulk-txt">{{ bulkText }}</span>
                <div class="dd-bulk-spacer"></div>
                <button type="button"
                        class="dd-bulk-btn"
                        [disabled]="!selections.length"
                        (click)="clearPicks()">
                    Clear picks
                </button>
                <button type="button"
                        class="dd-bulk-btn primary"
                        [disabled]="!filteredPeople.length"
                        (click)="toggleAllVisible()">
                    {{ selectAllButtonText }}
                </button>
            </div>
        </ng-template>

        <!-- Overlay mode (default): floats, escapes card/dialog clipping. -->
        <ng-template
            cdkConnectedOverlay
            [cdkConnectedOverlayOrigin]="ddTrigger"
            [cdkConnectedOverlayOpen]="isOpen && !disabled && !renderInline"
            [cdkConnectedOverlayHasBackdrop]="false"
            [cdkConnectedOverlayPositions]="overlayPositions"
            [cdkConnectedOverlayViewportMargin]="8"
            cdkConnectedOverlayPanelClass="people-picker-overlay"
            (attach)="onOverlayAttach()"
            (overlayOutsideClick)="onOverlayOutsideClick($event)">
            <div class="dd" (mousedown)="onDropdownMousedown($event)">
                <ng-container *ngTemplateOutlet="ddPanelBody"></ng-container>
            </div>
        </ng-template>

        <!-- Inline mode (dialog pickers): in flow beneath the field; the dialog owns the scroll. -->
        <div class="dd dd--inline"
             *ngIf="renderInline && isOpen && !disabled"
             (mousedown)="onDropdownMousedown($event)">
            <ng-container *ngTemplateOutlet="ddPanelBody"></ng-container>
        </div>
    </div>

    <!-- Helper text -->
    <div class="people-picker-helper" *ngIf="helperText">
        {{ helperText }}
    </div>
</div>
